// @flow

import React from 'react';
import styled from 'styled-components';
import { colors } from '../constants';

export type StatusNames =
  | 'COMPLETED'
  | 'ON'
  | 'OFF'
  | 'ERROR'
  | 'PENDING'
  | 'DISABLED';

type StatusMapping = {
  [key: StatusNames]: { dotColor: string, defaultText: string },
};

export const STATUS_FLAVORS: StatusMapping = {
  COMPLETED: {
    dotColor: colors.darkBlue,
    defaultText: 'Completed',
  },
  ON: {
    dotColor: colors.mediumGreenhouseGreen,
    defaultText: 'On',
  },
  OFF: {
    dotColor: colors.mediumGrey,
    defaultText: 'Off',
  },
  ERROR: {
    dotColor: colors.darkRed,
    defaultText: 'Error',
  },
  PENDING: {
    dotColor: colors.darkYellow,
    defaultText: 'Pending',
  },
  DISABLED: {
    dotColor: colors.mediumGrey,
    defaultText: 'Disabled',
  },
};

type Props = {
  /**
   * Enum that represents that status to indicate.
   */
  status: StatusNames,
  /**
   * Overwrites the default text, if provided.
   */
  text?: string,
  /**
   * Optional classname that is set on the Status Indicator's container.
   */
  className?: string,
  /**
   * If set to true, removes the left margin. Defaults to a falsey value.
   */
  compact?: boolean,
};

class StatusIndicator extends React.Component<Props> {
  static defaultProps = {
    status: 'ON',
  };

  render() {
    const { status, compact } = this.props;
    const flavor = STATUS_FLAVORS[status];
    const text = this.props.text || flavor.defaultText;

    return (
      <CenteredSpan className={this.props.className}>
        <IndicatorDot dotColor={flavor.dotColor} compact={compact} />
        {status === 'DISABLED' ? (
          <DisabledIndicatorText>{text}</DisabledIndicatorText>
        ) : (
          <IndicatorText>{text}</IndicatorText>
        )}
      </CenteredSpan>
    );
  }
}

const IndicatorDot = styled.span`
  ${({ dotColor }) => {
    return `background-color: ${dotColor};`;
  }}

  ${(props) => (props.compact ? '' : 'margin-left: 7px;')}
  margin-right: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  vertical-align: middle;
`;

const IndicatorText = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 14px;
  font-weight: 400;
`;

const DisabledIndicatorText = styled(IndicatorText)`
  color: ${colors.mediumGrey};
`;

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.92.0. To view the error, delete this comment and run Flow. */
const CenteredSpan = styled.span`
  display: flex;
  align-items: center;
`;

export default StatusIndicator;
type StatusIndicatorProps = Props;

export type { StatusIndicatorProps };
